import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { apiGetMentals, apiGetCyclePhases, apiGetCycles, apiGetSports, apiGetPhaseInsights } from '../services/api';
import PageHeader from '../components/PageHeader';
import PeriodSelector, { readStoredPeriod } from '../components/PeriodSelector';
import ViewToggle from '../components/ViewToggle';
import { filterLastNDays, filterWindow, periodWindow } from '../utils/dateRange';
import PeriodPager, { usePeriodOffset } from '../components/PeriodPager';
import { ReactComponent as EvolutionIcon } from '../assets/common/evolution_icon.svg';
import { ReactComponent as Calendrier2Icon } from '../assets/mental/calendrier2_icon.svg';
import { ReactComponent as RepartitionIcon } from '../assets/mental/repartition_icon.svg';
import { ReactComponent as Symptome3Icon } from '../assets/mental/symptome3_icon.svg';
import InfoButton from '../components/InfoButton';
import { PHASE_API_MAP, PHASE_COLORS_MAP, MENTAL, UI } from '../constants/colors';
import { symptomEmoji } from '../constants/emotionalSymptoms';
import SymptomBar from '../components/SymptomBar';
import ErrorBanner from '../components/ErrorBanner';
import './Mental.css';
import { toISODate } from '../utils/formDate';
import { MOOD_EMOJI } from '../constants/labels';
import DayDetailModal from '../components/DayDetailModal';
import { todayISO } from '../utils/formDate';
import PhaseProfile from '../components/PhaseProfile';


// Emojis des ressentis : voir constants/emotionalSymptoms.js.

const PHASE_LABELS_MENTAL ={ regles: 'Règles', folliculaire: 'Folliculaire', ovulation: 'Ovulation', luteale: 'Lutéale' };
const PHASE_KEYS_MENTAL = ['regles', 'luteale', 'folliculaire', 'ovulation'];
const PHASE_API_MAP_MENTAL = PHASE_API_MAP;   // map partagée, voir constants/colors.js

const renderStateChart = (data, phases, heightPx = 292) => {
  if (!data || data.length === 0) return <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Aucune donnée</p>;
  return (
    <svg width="100%" viewBox={`0 0 420 ${heightPx}`} style={{ display: 'block', height: 'auto' }}>
      {[0, 2, 4, 6, 8, 10].map(v => {
        const y = (heightPx - 47) - (v / 10) * (heightPx - 72);
        return (
          <g key={v}>
            <text x="26" y={y + 5} fontSize="13" fill={UI.textAxis} fontWeight="400" textAnchor="end">{v}</text>
            <line x1="32" y1={y} x2="390" y2={y} stroke="var(--color-grid)" strokeWidth="1" strokeDasharray="4,4" />
          </g>
        );
      })}
      {(() => {
        const n = data.length;
        const labelStep = n <= 7 ? 1 : n <= 15 ? 2 : n <= 30 ? 3 : 7;
        return data.map((d, i) => {
          const x = 32 + (i * (358 / Math.max(n - 1, 1)));
          const dayLabel = ['D', 'L', 'M', 'M', 'J', 'V', 'S'][new Date(d.date).getDay()];
          const phaseKey = phases[String(d.date)];
          const phaseColor = phaseKey ? (PHASE_COLORS_MAP[phaseKey] || UI.surfaceEmpty) : UI.surfaceEmpty;
          return (
            <g key={i}>
              {i % labelStep === 0 && <text x={x} y={heightPx - 20} fontSize="12" fill={UI.textAxis} textAnchor="middle">{dayLabel}</text>}
              <circle cx={x} cy={heightPx - 7} r="4" fill={phaseColor} />
            </g>
          );
        });
      })()}
      {['concentration', 'productivity', 'selfEsteem'].map((field, idx) => {
        const color = [MENTAL.concentration, MENTAL.productivite, MENTAL.estime][idx];
        const pts = data.map((d, i) => {
          const val = d[field]; if (val == null) return null;
          const x = 32 + (i * (358 / Math.max(data.length - 1, 1)));
          const y = (heightPx - 47) - ((val / 10) * (heightPx - 72));
          return [x, y];
        }).filter(Boolean);
        if (pts.length === 0) return null;
        let pathD;
        if (pts.length === 1) pathD = null;
        else if (pts.length === 2) pathD = `M${pts[0][0]},${pts[0][1]} L${pts[1][0]},${pts[1][1]}`;
        else {
          pathD = `M${pts[0][0]},${pts[0][1]}`;
          for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(i-1,0)], p1 = pts[i], p2 = pts[i+1], p3 = pts[Math.min(i+2,pts.length-1)];
            const cp1x = p1[0]+(p2[0]-p0[0])/6, cp1y = p1[1]+(p2[1]-p0[1])/6;
            const cp2x = p2[0]-(p3[0]-p1[0])/6, cp2y = p2[1]-(p3[1]-p1[1])/6;
            pathD += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
          }
        }
        return (
          <g key={field}>
            {pathD && <path d={pathD} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />}
            {pts.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="6" fill={color} />)}
          </g>
        );
      })}
    </svg>
  );
};

const Mental = () => {
  const { user, getToken } = useAuth();
  const [mentals, setMentals] = useState([]);
  // Le calendrier d'humeur ouvre le même aperçu que celui de la page Cycle : il lui
  // faut donc les trois domaines, pas seulement le mental.
  const [cycles, setCycles] = useState([]);
  const [sports, setSports] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [insights, setInsights] = useState([]);
  const [phases, setPhases] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupByPhase, setGroupByPhase] = useState(true);
  const [historyChart, setHistoryChart] = useState(null);
  const [historyRange, setHistoryRange] = useState(90);

   const [stateRangeDays, setStateRangeDays] = useState(() => readStoredPeriod('period:mental:state', 7));
   const [distRangeDays, setDistRangeDays] = useState(() => readStoredPeriod('period:mental:dist', 30));
   const [symptomsRangeDays, setSymptomsRangeDays] = useState(() => readStoredPeriod('period:mental:symptoms', 30));
   const [feelingRangeDays, setFeelingRangeDays] = useState(() => readStoredPeriod('period:mental:feeling', 30));

  // Période affichée par widget : 0 = celle qui finit aujourd'hui, 1 = la précédente…
  // (flèches ‹ › et glissement au doigt, cf. PeriodPager).
  const [stateOffset, setStateOffset] = usePeriodOffset(stateRangeDays);
  const [distOffset, setDistOffset] = usePeriodOffset(distRangeDays);
  const [symptomsOffset, setSymptomsOffset] = usePeriodOffset(symptomsRangeDays);
  const [feelingOffset, setFeelingOffset] = usePeriodOffset(feelingRangeDays);
  const [groupFeelingByPhase, setGroupFeelingByPhase] = useState(false);

  const now = new Date();

  const loadMentalData = useCallback(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    setLoading(true);
    setError(null);
    Promise.all([
      apiGetMentals(user.id, token),
      apiGetCyclePhases(user.id, token).catch(() => []),
      apiGetCycles(user.id, token).catch(() => []),
      apiGetSports(user.id, token).catch(() => []),
      apiGetPhaseInsights(user.id, token).catch(() => []),
    ]).then(([mentalData, phaseData, cycleData, sportData, insightData]) => {
      setMentals(mentalData);
      setCycles(cycleData || []);
      setSports(sportData || []);
      setInsights(insightData || []);
      const lookup = {};
      (phaseData || []).forEach(p => {
        lookup[String(p.date)] = PHASE_API_MAP_MENTAL[p.phase] || p.phase.toLowerCase();
      });
      setPhases(lookup);
    }).catch(() => setError('Impossible de charger tes données. Vérifie ta connexion et réessaie.'))
      .finally(() => setLoading(false));
  }, [user?.id, getToken]);

  useEffect(() => {
    loadMentalData();
  }, [loadMentalData]);

   const mentalsByDateAsc = useMemo(() => {
     return [...mentals].sort((a, b) => String(a.date).localeCompare(String(b.date)));
   }, [mentals]);

   // Premier relevé : borne du passé pour la navigation par période.
   const earliestMental = mentalsByDateAsc[0]?.date ?? null;

   const mentalsStateRange = useMemo(
     () => filterWindow(mentalsByDateAsc, periodWindow(stateRangeDays, stateOffset), (m) => m.date),
     [mentalsByDateAsc, stateRangeDays, stateOffset]
   );

   const mentalsDistRange = useMemo(
     () => filterWindow(mentalsByDateAsc, periodWindow(distRangeDays, distOffset), (m) => m.date),
     [mentalsByDateAsc, distRangeDays, distOffset]
   );

   const mentalsSymptomsRange = useMemo(
     () => filterWindow(mentalsByDateAsc, periodWindow(symptomsRangeDays, symptomsOffset), (m) => m.date),
     [mentalsByDateAsc, symptomsRangeDays, symptomsOffset]
   );

  // Build last 30 days array with dates and emojis
  const last30Days = useMemo(() => {
    const mentalMap = {};
    mentals.forEach(m => {
      mentalMap[m.date] = m.mood ? (MOOD_EMOJI[m.mood] ?? '😶') : '😶';
    });
    
    return Array.from({ length: 30 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (29 - i));
      const dateStr = toISODate(d);
      return {
        day: d.getDate(),
        dateStr,
        emoji: mentalMap[dateStr] ?? '',
      };
    });
  }, [mentals]);

  // Mood distribution - categorize into 3 groups
  const moodCategories = {
    'Positive': ['Calme', 'Heureuse', 'Énergique'],
    'Difficile': ['Irritable', 'Triste', 'Angoissée', 'Déprimée', 'Autocritique'],
    'Variable': ["Sautes d'humeur", "Manque d'énergie"],
  };

  const categoryCounts = {
    'Positive': 0,
    'Difficile': 0,
    'Variable': 0,
  };
  
  mentalsDistRange.forEach(m => {
    if (!m.mood) return;
    for (const [category, moods] of Object.entries(moodCategories)) {
      if (moods.includes(m.mood)) {
        categoryCounts[category]++;
        break;
      }
    }
  });
  
  const totalMoods = Object.values(categoryCounts).reduce((a, b) => a + b, 0);
  const moodDist = Object.entries(categoryCounts).map(([category, count]) => ({
    label: category,
    count,
    pct: totalMoods ? Math.round(count / totalMoods * 100) : 0,
  }));

  // Emotional symptoms frequency (selected period)
  const symptomFreq = {};
  mentalsSymptomsRange.forEach(m => {
    (m.emotional_symptoms || []).forEach(s => {
      symptomFreq[s] = (symptomFreq[s] || 0) + 1;
    });
  });

  // Symptom frequency over 3× the selected period (for average baseline)
  // Référence sur 3 périodes, arrêtée au même jour que la période affichée.
  const mentalsTripleRange = useMemo(
    () => filterWindow(
      mentalsByDateAsc,
      periodWindow(symptomsRangeDays, symptomsOffset, symptomsRangeDays * 3),
      (m) => m.date,
    ),
    [mentalsByDateAsc, symptomsRangeDays, symptomsOffset]
  );
  const tripleSymptomFreq = {};
  mentalsTripleRange.forEach(m => {
    (m.emotional_symptoms || []).forEach(s => {
      tripleSymptomFreq[s] = (tripleSymptomFreq[s] || 0) + 1;
    });
  });

  const symptomList = Object.entries(symptomFreq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([label, days]) => ({
      emoji: symptomEmoji(label),
      label,
      days,
      avgDays: Math.round((tripleSymptomFreq[label] || 0) / 3),
    }));

  // ── Ressenti de la journée ────────────────────────────────────────────────
  // Affiché comme les ressentis émotionnels (barres), mais calculé à part : c'est
  // un choix unique parmi trois, là où les ressentis sont multi-sélection. Les
  // mélanger dans la même liste donnerait des pourcentages qui ne se comparent pas.
  const DAY_FEELINGS = useMemo(() => ([
    { label: 'Bonne', emoji: '😊' },
    { label: 'Normale', emoji: '😐' },
    { label: 'Mauvaise', emoji: '😞' },
  ]), []);

  const mentalsFeelingRange = useMemo(
    () => filterWindow(mentalsByDateAsc, periodWindow(feelingRangeDays, feelingOffset), (m) => m.date),
    [mentalsByDateAsc, feelingRangeDays, feelingOffset]
  );
  const mentalsFeelingTripleRange = useMemo(
    () => filterWindow(
      mentalsByDateAsc,
      periodWindow(feelingRangeDays, feelingOffset, feelingRangeDays * 3),
      (m) => m.date,
    ),
    [mentalsByDateAsc, feelingRangeDays, feelingOffset]
  );

  // Le ressenti se saisit désormais en deux temps. Un relevé d'avant le découpage n'a
  // qu'un `day_feeling` : il est rangé à part plutôt que réparti arbitrairement sur un
  // moment, ce qui inventerait une saisie que la sportive n'a pas faite.
  const FEELING_MOMENTS = useMemo(() => ([
    { key: 'day_feeling_morning', label: 'Matin' },
    { key: 'day_feeling_afternoon', label: 'Après-midi' },
    { key: 'day_feeling', label: 'Journée entière' },
  ]), []);

  const countFeelings = (records, key) => {
    const counts = {};
    records.forEach((m) => {
      const value = m[key];
      if (value) counts[value] = (counts[value] || 0) + 1;
    });
    return counts;
  };

  /** Une liste de barres par moment, les moments jamais renseignés étant écartés. */
  const buildFeelingBlocks = (records, tripleRecords) =>
    FEELING_MOMENTS.map(({ key, label }) => {
      const counts = countFeelings(records, key);
      const tripleCounts = countFeelings(tripleRecords, key);
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      return {
        key,
        label,
        total,
        list: DAY_FEELINGS
          .map(({ label: feeling, emoji }) => ({
            label: feeling,
            emoji,
            days: counts[feeling] || 0,
            avgDays: Math.round((tripleCounts[feeling] || 0) / 3),
          }))
          .filter((f) => f.days > 0),
      };
    }).filter((block) => block.total > 0);

  const feelingBlocks = useMemo(
    () => buildFeelingBlocks(mentalsFeelingRange, mentalsFeelingTripleRange),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mentalsFeelingRange, mentalsFeelingTripleRange, DAY_FEELINGS, FEELING_MOMENTS]
  );

  const feelingTotal = feelingBlocks.reduce((sum, block) => sum + block.total, 0);

  // Groupement par phase : contrairement aux ressentis émotionnels, rangés selon
  // une table statique label → phase, on compte ici les journées **là où elles
  // sont réellement tombées**, via la timeline des phases. Une journée pèse jusqu'à
  // deux observations, une par moment renseigné.
  const feelingByPhase = useMemo(() => {
    const byPhase = {};
    mentalsFeelingRange.forEach((m) => {
      const phaseKey = phases[String(m.date)];
      if (!phaseKey) return;
      const values = [m.day_feeling_morning, m.day_feeling_afternoon].filter(Boolean);
      // Repli : un relevé d'avant le découpage ne compte qu'une fois, pour ne pas peser
      // le double d'une journée moderne où un seul moment est renseigné.
      if (!values.length && m.day_feeling) values.push(m.day_feeling);
      values.forEach((value) => {
        byPhase[phaseKey] = byPhase[phaseKey] || {};
        byPhase[phaseKey][value] = (byPhase[phaseKey][value] || 0) + 1;
      });
    });
    return byPhase;
  }, [mentalsFeelingRange, phases]);

  // Même principe que `feelingByPhase` : on compte les ressentis là où ils sont
  // réellement tombés. Le regroupement passait par un catalogue statique où
  // « Fatigue » figurait sous Règles *et* Lutéale — le saisir une fois pendant les
  // règles le faisait apparaître sous une phase non encore vécue.
  const symptomsByPhase = useMemo(() => {
    const counts = {};
    const phaseDays = {};
    mentalsSymptomsRange.forEach((m) => {
      const phaseKey = phases[String(m.date)];
      if (!phaseKey) return;
      phaseDays[phaseKey] = (phaseDays[phaseKey] || 0) + 1;
      (m.emotional_symptoms || []).forEach((label) => {
        counts[phaseKey] = counts[phaseKey] || {};
        counts[phaseKey][label] = (counts[phaseKey][label] || 0) + 1;
      });
    });
    return { counts, phaseDays };
  }, [mentalsSymptomsRange, phases]);

  const chartData = useMemo(() => {
    return mentalsStateRange.map(m => ({
      date: m.date,
      concentration: m.concentration,
      productivity: m.productivity,
      selfEsteem: m.self_esteem,
    }));
  }, [mentalsStateRange]);

  const historyChartData = useMemo(() => {
    return filterLastNDays(mentalsByDateAsc, historyRange, m => m.date).map(m => ({
      date: m.date,
      concentration: m.concentration,
      productivity: m.productivity,
      selfEsteem: m.self_esteem,
    }));
  }, [mentalsByDateAsc, historyRange]);

  const SYMPTOM_COLORS = [MENTAL.concentration, MENTAL.productivite, MENTAL.estime];

  const DIST_COLORS = {
    'Positive': MENTAL.concentration,
    'Difficile': MENTAL.productivite,
    'Variable':  MENTAL.estime,
  };

  return (
    <div className="mental-page">
      <PageHeader title="Mental" page="mental" />

      {error && <ErrorBanner message={error} onRetry={loadMentalData} />}

      {/* Widget État mental */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--evo">
            <EvolutionIcon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3
              className="mental-page__chart-title-clickable"
              onClick={() => setHistoryChart('state')}
            >État mental<InfoButton infoKey="evolutionMentalChart" /></h3>
          </div>
          <PeriodSelector
            value={stateRangeDays}
            onChange={setStateRangeDays}
            storageKey="period:mental:state"
            className="mental-page__widget-badge"
            arrowClassName="mental-page__badge-arrow"
          />
        </div>
        <PeriodPager days={stateRangeDays} offset={stateOffset} onOffsetChange={setStateOffset} earliest={earliestMental}>
          <div style={{ padding: '0.25rem 0 0 0' }}>
            {renderStateChart(chartData, phases)}
            <div className="mental-page__legend">
              <div className="mental-page__legend-item">
                <span className="mental-page__legend-dot" style={{ background: MENTAL.concentration }} />
                <span>Concentration</span>
              </div>
              <div className="mental-page__legend-item">
                <span className="mental-page__legend-dot" style={{ background: MENTAL.productivite }} />
                <span>Productivité</span>
              </div>
              <div className="mental-page__legend-item">
                <span className="mental-page__legend-dot" style={{ background: MENTAL.estime }} />
                <span>Estime de soi</span>
              </div>
            </div>
          </div>
        </PeriodPager>
      </div>

      {/* Profil par phase — sport et mental croisés avec le cycle, sur 6 mois */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--rep">
            <RepartitionIcon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3>Ton profil par phase</h3>
            <p className="mental-page__widget-subtitle">Sur tes 6 derniers mois</p>
          </div>
        </div>
        <PhaseProfile insights={insights} domain="mental" />
      </div>

      {/* Mood calendar widget */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--cal">
            <Calendrier2Icon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3>Calendrier d'humeur</h3>
            <p className="mental-page__widget-subtitle">30 derniers jours</p>
          </div>
        </div>
        <div className="mental-page__mood-grid">
          {last30Days.map((d, idx) => (
            <div
              key={idx}
              className="mental-page__mood-cell mental-page__mood-cell--clickable"
              role="button"
              tabIndex={0}
              onClick={() => setSelectedDate(d.dateStr)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDate(d.dateStr); } }}
            >
              <span className="mental-page__mood-day">{d.day}</span>
              <span className="mental-page__mood-emoji">{d.emoji}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Widget Répartition du mois */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--rep">
            <RepartitionIcon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3>Répartition des humeurs<InfoButton infoKey="repartitionMois" /></h3>
          </div>
          <PeriodSelector
            value={distRangeDays}
            onChange={setDistRangeDays}
            storageKey="period:mental:dist"
            className="mental-page__widget-badge"
            arrowClassName="mental-page__badge-arrow"
          />
        </div>
        <PeriodPager days={distRangeDays} offset={distOffset} onOffsetChange={setDistOffset} earliest={earliestMental}>
          {totalMoods === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '2rem 0' }}>Aucune donnée</p>
          ) : (
            <>
              <div style={{ width: '100%', maxWidth: '240px', height: 200, margin: '0 auto' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={moodDist.filter(d => d.count > 0)}
                      dataKey="pct"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={88}
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                      isAnimationActive={false}
                    >
                      {moodDist.filter(d => d.count > 0).map((item, i) => (
                        <Cell key={i} fill={DIST_COLORS[item.label]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', maxWidth: '240px', width: '100%', margin: '8px auto 0' }}>
                {['Positive', 'Difficile', 'Variable'].map((category) => {
                  const item = moodDist.find(d => d.label === category);
                  const pct = item ? item.pct : 0;
                  return (
                    <div key={category} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', background: DIST_COLORS[category], borderRadius: '50%', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 500, color: UI.textLabel, whiteSpace: 'nowrap' }}>
                        {category} <span style={{ color: DIST_COLORS[category], fontWeight: 700 }}>{pct}%</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </PeriodPager>
      </div>

      {/* Widget Symptômes les plus fréquents */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--sym">
            <Symptome3Icon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3>Ressentis les plus fréquents</h3>
          </div>
          <div className="view-controls">
            <ViewToggle grouped={groupByPhase} onChange={setGroupByPhase} />
            <PeriodSelector
              value={symptomsRangeDays}
              onChange={setSymptomsRangeDays}
              storageKey="period:mental:symptoms"
              className="mental-page__widget-badge"
              arrowClassName="mental-page__badge-arrow"
            />
          </div>
        </div>
        <PeriodPager days={symptomsRangeDays} offset={symptomsOffset} onOffsetChange={setSymptomsOffset} earliest={earliestMental}>
          {symptomList.length === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>Aucun ressenti enregistré</p>
          ) : groupByPhase ? (
            <div className="phase-groups">
              {PHASE_KEYS_MENTAL.map(phaseKey => {
                const recorded = symptomsByPhase.counts[phaseKey] || {};
                const phaseSymptoms = Object.entries(recorded)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, days]) => ({ emoji: symptomEmoji(label), label, days, avgDays: 0 }));
                if (phaseSymptoms.length === 0) return null;
                const phaseColor = PHASE_COLORS_MAP[phaseKey];
                return (
                  <div key={phaseKey} className="phase-bar" style={{ '--phase-bar-color': phaseColor }}>
                    <span className="phase-group-label" style={{ color: phaseColor }}>
                      {PHASE_LABELS_MENTAL[phaseKey]}
                    </span>
                    <div className="symptom-bar-list">
                      {phaseSymptoms.map((s, i) => (
                        <SymptomBar
                          key={i}
                          emoji={s.emoji}
                          label={s.label}
                          days={s.days}
                          accentColor={MENTAL.concentration}
                          pct={Math.min(100, Math.round((s.days / symptomsRangeDays) * 100))}
                          avgPct={Math.min(100, Math.round((s.avgDays / symptomsRangeDays) * 100))}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="symptom-bar-list">
              {symptomList.map((s, i) => (
                <SymptomBar
                  key={i}
                  emoji={s.emoji}
                  label={s.label}
                  days={s.days}
                  accentColor={SYMPTOM_COLORS[i % 3]}
                  pct={Math.min(100, Math.round((s.days / symptomsRangeDays) * 100))}
                  avgPct={Math.min(100, Math.round((s.avgDays / symptomsRangeDays) * 100))}
                />
              ))}
            </div>
          )}
        </PeriodPager>
      </div>
      {/* Widget Ressenti de la journée */}
      <div className="mental-page__widget">
        <div className="mental-page__widget-header">
          <div className="mental-page__widget-icon mental-page__widget-icon--sym">
            <Symptome3Icon />
          </div>
          <div className="mental-page__widget-title-group">
            <h3>Ressenti de la journée</h3>
            <p className="mental-page__widget-subtitle">Tes matinées et tes après-midis</p>
          </div>
          <div className="view-controls">
            <ViewToggle grouped={groupFeelingByPhase} onChange={setGroupFeelingByPhase} />
            <PeriodSelector
              value={feelingRangeDays}
              onChange={setFeelingRangeDays}
              storageKey="period:mental:feeling"
              className="mental-page__widget-badge"
              arrowClassName="mental-page__badge-arrow"
            />
          </div>
        </div>
        <PeriodPager days={feelingRangeDays} offset={feelingOffset} onOffsetChange={setFeelingOffset} earliest={earliestMental}>
          {feelingTotal === 0 ? (
            <p style={{ textAlign: 'center', color: UI.textEmpty, padding: '1rem 0' }}>
              Aucun ressenti de journée enregistré
            </p>
          ) : groupFeelingByPhase ? (
            <div className="phase-groups">
              {PHASE_KEYS_MENTAL.map((phaseKey) => {
                const counts = feelingByPhase[phaseKey];
                if (!counts) return null;
                const phaseTotal = Object.values(counts).reduce((a, b) => a + b, 0);
                const phaseColor = PHASE_COLORS_MAP[phaseKey];
                return (
                  <div key={phaseKey} className="phase-bar" style={{ '--phase-bar-color': phaseColor }}>
                    <span className="phase-group-label" style={{ color: phaseColor }}>
                      {PHASE_LABELS_MENTAL[phaseKey]}
                    </span>
                    <div className="symptom-bar-list">
                      {DAY_FEELINGS.filter((f) => counts[f.label]).map((f, i) => (
                        <SymptomBar
                          key={f.label}
                          emoji={f.emoji}
                          label={f.label}
                          days={counts[f.label]}
                          accentColor={SYMPTOM_COLORS[i % 3]}
                          // Rapporté au nombre de jours *de cette phase* et non à la
                          // période entière : sinon une phase courte plafonnerait bas.
                          pct={Math.round((counts[f.label] / phaseTotal) * 100)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Un bloc par moment de la journée : mélanger matin et après-midi dans une
               seule liste rendrait les pourcentages incomparables, chaque journée pesant
               deux fois. */
            <div className="phase-groups">
              {feelingBlocks.map((block) => (
                <div key={block.key} className="mental-page__symptom-group--moment phase-bar">
                  <span className="phase-group-label">{block.label}</span>
                  <div className="symptom-bar-list">
                    {block.list.map((f, i) => (
                      <SymptomBar
                        key={f.label}
                        emoji={f.emoji}
                        label={f.label}
                        days={f.days}
                        accentColor={SYMPTOM_COLORS[i % 3]}
                        // Part des moments renseignés, pas de la période : les jours sans
                        // saisie ne sont pas des journées « ni bonnes ni mauvaises ».
                        pct={Math.round((f.days / block.total) * 100)}
                        avgPct={Math.min(100, Math.round((f.avgDays / feelingRangeDays) * 100))}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </PeriodPager>
      </div>

      {/* History modal */}
      {historyChart && (
        <div className="mental-page__history-overlay" onClick={() => setHistoryChart(null)}>
          <div className="mental-page__history-modal" onClick={e => e.stopPropagation()}>
            <div className="mental-page__history-header">
              <h3 className="mental-page__history-title">Historique — État mental</h3>
              <button className="mental-page__history-close" onClick={() => setHistoryChart(null)} aria-label="Fermer">✕</button>
            </div>
            <div className="mental-page__history-periods">
              {[90, 180, 365].map(r => (
                <button
                  key={r}
                  className={`mental-page__history-period${historyRange === r ? ' mental-page__history-period--active' : ''}`}
                  onClick={() => setHistoryRange(r)}
                >
                  {r === 365 ? '1 an' : `${r}j`}
                </button>
              ))}
            </div>
            {renderStateChart(historyChartData, phases, 260)}
            <div className="mental-page__legend" style={{ marginTop: 8, paddingTop: 8 }}>
              {[[MENTAL.concentration, 'Concentration'], [MENTAL.productivite, 'Productivité'], [MENTAL.estime, 'Estime de soi']].map(([color, label]) => (
                <div key={label} className="mental-page__legend-item">
                  <span className="mental-page__legend-dot" style={{ background: color }} />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedDate && (
        <DayDetailModal
          date={selectedDate}
          entries={{
            cycle: cycles.find((c) => c.date === selectedDate),
            mental: mentals.find((m) => m.date === selectedDate),
            sport: sports.find((sp) => sp.date === selectedDate),
          }}
          phaseLabel={phases[selectedDate] ? PHASE_LABELS_MENTAL[phases[selectedDate]] : null}
          phaseColor={phases[selectedDate] ? PHASE_COLORS_MAP[phases[selectedDate]] : null}
          isFuture={selectedDate > todayISO()}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
};

export default Mental;
